import {
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextFieldProps,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
} from '@mui/material';
import { FileDownloadOutlined, SearchOutlined, RestartAlt } from '@mui/icons-material';
import strings from 'common/Translation/Translate';
import DateInput from 'components/DateInput/DateInput';
import dayjs from 'dayjs';
import {
  FC,
  JSXElementConstructor,
  ReactElement,
  useCallback,
  useEffect,
  useState,
  MouseEvent,
  type ReactNode,
} from 'react';
import { ToolTip } from 'components/Tooltip/ToolTip';
import { getCurrentIndianFinancialYearRange } from './indianFinancialYear';

interface DateRange {
  startDate: string;
  endDate: string;
}

interface ExportItem {
  label: string;
  onClick: () => void;
}

interface OwnProps {
  handleDates: (dates: DateRange) => void;
  onSearch?: (dates?: DateRange) => void;
  onReset?: () => void;
  exports?: ExportItem[];
  resetKey?: number;
  /** Rendered in the actions row after Search (e.g. consolidated growth comparison settings). */
  trailingActions?: ReactNode;
}

interface Preset {
  key: string;
  label: string;
  getRange: () => DateRange;
}

const presets: Preset[] = [
  {
    key: 'this_month',
    label: 'This Month',
    getRange: () => ({
      startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
      endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    }),
  },
  {
    key: 'last_3m',
    label: 'Last 3M',
    getRange: () => ({
      startDate: dayjs().subtract(2, 'month').startOf('month').format('YYYY-MM-DD'),
      endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    }),
  },
  {
    key: 'last_6m',
    label: 'Last 6M',
    getRange: () => ({
      startDate: dayjs().subtract(5, 'month').startOf('month').format('YYYY-MM-DD'),
      endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    }),
  },
  {
    key: 'this_year',
    label: strings.reports_filter_preset_this_fy,
    getRange: () => getCurrentIndianFinancialYearRange(),
  },
];

const FIELD_HEIGHT = 40;

const Filter: FC<OwnProps> = ({ handleDates, onSearch, onReset, exports = [], resetKey = 0, trailingActions }) => {
  const [dates, setDates] = useState<DateRange>({
    startDate: dayjs().date(1).format('YYYY-MM-DD'),
    endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
  });

  const [activePreset, setActivePreset] = useState<string | null>('this_month');
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    handleDates(dates);
  }, [dates.startDate, dates.endDate, handleDates, dates]);

  useEffect(() => {
    setDates({
      startDate: dayjs().date(1).format('YYYY-MM-DD'),
      endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    });
    setActivePreset('this_month');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const handleDate = (value: string, field: string, _error: { error: boolean; message: string }) => {
    setActivePreset(null);
    if (field === 'startDate') {
      setDates({ ...dates, startDate: value });
    } else {
      setDates({ ...dates, endDate: value });
    }
  };

  const applyPreset = useCallback(
    (_event: MouseEvent<HTMLElement>, newVal: string | null) => {
      if (!newVal) return;
      const preset = presets.find(p => p.key === newVal);
      if (!preset) return;
      const range = preset.getRange();
      setDates(range);
      setActivePreset(newVal);
      handleDates(range);
      onSearch?.(range);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [handleDates, onSearch],
  );

  return (
    <Box
      sx={{
        background: '#fafafa',
        border: '1px solid #eceff1',
        borderRadius: '16px',
        px: { xs: 1, sm: 2.5 },
        py: { xs: 1.25, sm: 2 },
        mb: 2.5,
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        alignItems: { xs: 'stretch', lg: 'center' },
        gap: 2,
      }}>
      {/* LEFT: Date range */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.5 }, flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
        <Box sx={{ flex: { xs: '1 1 0', sm: '0 0 auto' }, minWidth: 0, width: { xs: 'auto', sm: 160 }, '& .MuiInputBase-root': { height: FIELD_HEIGHT } }}>
          <DateInput
            size="small"
            field="startDate"
            label={strings.common_text_startDate}
            value={dates.startDate}
            textChange={handleDate as any}
            onChange={function (value: any, keyboardInputValue?: string | undefined): void {
              throw new Error(strings.functionNotImplementedError);
            }}
            renderInput={function (props: TextFieldProps): ReactElement<any, string | JSXElementConstructor<any>> {
              throw new Error(strings.functionNotImplementedError);
            }}
          />
        </Box>
        <Box sx={{ color: 'text.disabled', fontSize: '0.85rem', fontWeight: 500 }}>→</Box>
        <Box sx={{ flex: { xs: '1 1 0', sm: '0 0 auto' }, minWidth: 0, width: { xs: 'auto', sm: 160 }, '& .MuiInputBase-root': { height: FIELD_HEIGHT } }}>
          <DateInput
            size="small"
            field="endDate"
            label={strings.common_text_endDate}
            value={dates.endDate}
            textChange={handleDate as any}
            onChange={function (value: any, keyboardInputValue?: string | undefined): void {
              throw new Error(strings.functionNotImplementedError);
            }}
            renderInput={function (props: TextFieldProps): ReactElement<any, string | JSXElementConstructor<any>> {
              throw new Error(strings.functionNotImplementedError);
            }}
          />
        </Box>
      </Box>

      {/* CENTER: Segmented control */}
      <Box sx={{ flex: '1 1 auto', display: 'flex', justifyContent: { xs: 'flex-start', sm: 'center' }, minWidth: 0, overflowX: 'auto' }}>
        <ToggleButtonGroup
          value={activePreset}
          exclusive
          onChange={applyPreset}
          size="small"
          sx={{
            backgroundColor: '#f1f5f9',
            borderRadius: '8px',
            border: '1px solid #cfd8dc',
            p: '3px',
            '& .MuiToggleButtonGroup-grouped': {
              border: 'none !important',
              borderRadius: '6px !important',
              m: 0,
            },
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.76rem',
              whiteSpace: 'nowrap',
              px: 1.5,
              py: 0.5,
              height: 32,
              lineHeight: 1,
              border: 'none',
              borderRadius: '6px',
              color: '#64748b',
              transition: 'all 0.15s ease',
              '&.Mui-selected': {
                backgroundColor: '#1e3a5f',
                color: '#fff',
                fontWeight: 600,
                boxShadow: '0 1px 3px rgba(30,58,95,0.2)',
                '&:hover': {
                  backgroundColor: '#2c4a6f',
                },
              },
              '&:hover': {
                backgroundColor: '#e2e8f0',
              },
            },
          }}>
          {presets.map(p => (
            <ToggleButton key={p.key} value={p.key}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* RIGHT: Actions */}
      {(onSearch || onReset || exports.length > 0 || trailingActions) && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-end', sm: 'flex-start' }, gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
          {onReset && (
            <ToolTip title={`Reset`}>
              <IconButton
                size="small"
                aria-label="Growth comparison settings"
                onClick={onReset}
                sx={{
                  height: 40,
                  width: 40,
                  borderRadius: 1,
                  border: '1px solid #cfd8dc',
                  color: '#546e7a',
                  '&:hover': { backgroundColor: '#fafafa', borderColor: '#90a4ae' },
                }}>
                <RestartAlt sx={{ fontSize: 20 }} />
              </IconButton>
            </ToolTip>
          )}

          {onSearch && (
            <ToolTip title={`Search`}>
              <IconButton
                size="small"
                aria-label="search"
                onClick={() => onSearch()}
                sx={{
                  height: 40,
                  width: 40,
                  borderRadius: 1,
                  border: '1px solid #cfd8dc',
                  color: '#546e7a',
                  '&:hover': { backgroundColor: '#fafafa', borderColor: '#90a4ae' },
                }}>
                <SearchOutlined sx={{ fontSize: 20 }} />
              </IconButton>
            </ToolTip>
          )}
          {trailingActions}

          {exports.length > 0 && (
            <>
              <ToolTip title={`Export`}>
                <IconButton
                  size="small"
                  aria-label="export"
                  onClick={e => setExportAnchor(e.currentTarget)}
                  sx={{
                    height: 40,
                    width: 40,
                    borderRadius: 1,
                    border: '1px solid #cfd8dc',
                    color: '#546e7a',
                    '&:hover': { backgroundColor: '#fafafa', borderColor: '#90a4ae' },
                  }}>
                  <FileDownloadOutlined sx={{ fontSize: 20 }} />
                </IconButton>
              </ToolTip>

              <Menu
                anchorEl={exportAnchor}
                open={Boolean(exportAnchor)}
                onClose={() => setExportAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                  paper: {
                    sx: { mt: 0.5, minWidth: 200, borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
                  },
                }}>
                {exports.map((exp, i) => (
                  <MenuItem
                    key={i}
                    onClick={() => {
                      exp.onClick();
                      setExportAnchor(null);
                    }}
                    sx={{
                      fontSize: '12px',
                      py: 1,
                      borderBottom: exports.length - 1 === i ? 'none' : '1px solid #eceff1',
                    }}>
                    <ListItemIcon>
                      <FileDownloadOutlined sx={{ fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText
                      sx={{
                        '.MuiTypography-body1': {
                          fontSize: 14,
                        },
                      }}>
                      {exp.label}
                    </ListItemText>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Filter;
